/**
 * talepFiltreleri.js — the request-list filters of the organizer panel (backlog #458, 2026-10-17; backend #457) and their link with the URL.
 *
 * The filters (all optional): the kind of technical request (Elektrik / Su / Hava / İnternet), a kW range, "above the allowed kW", the request status and (#462) the
 * SOURCE of the request (who entered it: Firma / Katılımcı / Dekoratör / Organizatör / Personel), plus the search text. They live in the address (?tur=elektrik&kwMin=10&kwMax=40&asan=1&durum=0&kaynak=ORGANIZATOR&ara=A12) so a filtered list can be bookmarked, shared and survives a reload / back.
 * The staff request page (#77, not built yet) can use the same helpers and the same TalepFiltreleri component.
 *
 * The backend params: kind -> requestGroup=teknik + typeId (1 Elektrik, 2 Su, 3 Hava, 4 İnternet, StandTalepConstant), kwMin / kwMax (kW, inclusive), asanKw=true,
 * status 0 Bekliyor / 1 Onaylı / 2 Reddedildi, kaynak = the same word (FIRMA = KATILIMCI + DEKORATOR). The kW filters are about the Elektrik request: the backend implies the
 * Teknik group for them.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { kaynakFiltresiGecerli } from './talepKaynaklari';

/** The kinds, in chip order, with the backend's stand_talep_type_id. */
export const TALEP_TURLERI = [
  { kod: 'elektrik', typeId: 1 },
  { kod: 'su', typeId: 2 },
  { kod: 'hava', typeId: 3 },
  { kod: 'internet', typeId: 4 },
];

export const TALEP_DURUMLARI = ['0', '1', '2'];

const ANAHTARLAR = ['tur', 'kwMin', 'kwMax', 'asan', 'durum', 'kaynak', 'ara'];

/** A kW number as text from the address: a plain non-negative number, else ''. */
function kwMetni(v) {
  if (v === null || v === undefined) return '';
  const s = String(v).trim().replace(',', '.');
  if (s === '' || !/^[0-9]+(\.[0-9]+)?$/.test(s)) return '';
  return s;
}

/** URLSearchParams (or anything with .get) -> { tur, kwMin, kwMax, asan, durum, kaynak, ara }; every field '' / false when absent or not valid. */
export function filtreOku(params) {
  const al = (k) => (params && typeof params.get === 'function' ? params.get(k) : null);
  const tur = al('tur');
  const durum = al('durum');
  const kaynak = al('kaynak');
  return {
    tur: TALEP_TURLERI.some((t) => t.kod === tur) ? tur : '',
    kwMin: kwMetni(al('kwMin')),
    kwMax: kwMetni(al('kwMax')),
    asan: al('asan') === '1',
    durum: TALEP_DURUMLARI.includes(durum) ? durum : '',
    kaynak: kaynakFiltresiGecerli(kaynak) ? kaynak : '',
    ara: (al('ara') ?? '').trim(),
  };
}

/** A filter object -> URLSearchParams, keeping every parameter of `mevcut` that is not one of ours (empty values are left out, the address stays clean). */
export function filtreYaz(filtre, mevcut) {
  const out = new URLSearchParams(mevcut ? mevcut.toString() : '');
  ANAHTARLAR.forEach((k) => out.delete(k));
  const f = filtre || {};
  if (f.tur) out.set('tur', f.tur);
  if (kwMetni(f.kwMin) !== '') out.set('kwMin', kwMetni(f.kwMin));
  if (kwMetni(f.kwMax) !== '') out.set('kwMax', kwMetni(f.kwMax));
  if (f.asan) out.set('asan', '1');
  if (f.durum !== undefined && f.durum !== '' && TALEP_DURUMLARI.includes(String(f.durum))) out.set('durum', String(f.durum));
  if (kaynakFiltresiGecerli(f.kaynak)) out.set('kaynak', f.kaynak);
  if (f.ara && String(f.ara).trim() !== '') out.set('ara', String(f.ara).trim());
  return out;
}

/** The filter object -> the query parameters of the list endpoint (undefined = not sent). */
export function apiParametreleri(filtre) {
  const f = filtre || {};
  const tur = TALEP_TURLERI.find((t) => t.kod === f.tur);
  return {
    // a kW filter is about the Elektrik request: the backend implies the Teknik group, the chip is only for the other kinds
    requestGroup: tur ? 'teknik' : undefined,
    typeId: tur ? tur.typeId : undefined,
    kwMin: kwMetni(f.kwMin) !== '' ? Number(kwMetni(f.kwMin)) : undefined,
    kwMax: kwMetni(f.kwMax) !== '' ? Number(kwMetni(f.kwMax)) : undefined,
    asanKw: f.asan ? true : undefined,
    status: f.durum !== undefined && f.durum !== '' ? Number(f.durum) : undefined,
    // #462: who entered the request (the backend keeps the stands that have a request by that source and shows only those requests)
    kaynak: kaynakFiltresiGecerli(f.kaynak) ? f.kaynak : undefined,
  };
}

/** How many filters are on (the search text not counted): for the "clear" button and the badge. */
export function etkinFiltreSayisi(filtre) {
  const f = filtre || {};
  return [f.tur, kwMetni(f.kwMin), kwMetni(f.kwMax), f.asan ? '1' : '', f.durum, kaynakFiltresiGecerli(f.kaynak) ? f.kaynak : ''].filter((v) => v !== undefined && v !== '').length;
}

/**
 * The filters as state in the address. `degistir({kwMin: '10'})` changes some fields (the kW fields and "above the allowance" put the kind on Elektrik when
 * it is empty or another kind: they are electricity filters); `temizle()` removes them all. Changes REPLACE the history entry, so "back" leaves the page
 * instead of stepping through every keystroke of a filter.
 */
export function useTalepFiltreleri() {
  const [params, setParams] = useSearchParams();
  const filtre = useMemo(() => filtreOku(params), [params]);

  const degistir = useCallback((yama) => {
    setParams((onceki) => {
      const simdi = filtreOku(onceki);
      const yeni = { ...simdi, ...yama };
      const kwDegisti = 'kwMin' in yama || 'kwMax' in yama || 'asan' in yama;
      if (kwDegisti && (kwMetni(yeni.kwMin) !== '' || kwMetni(yeni.kwMax) !== '' || yeni.asan) && yeni.tur !== 'elektrik') yeni.tur = 'elektrik';
      return filtreYaz(yeni, onceki);
    }, { replace: true });
  }, [setParams]);

  const temizle = useCallback(() => {
    setParams((onceki) => filtreYaz({ ara: filtreOku(onceki).ara }, onceki), { replace: true });
  }, [setParams]);

  const apiParams = useMemo(() => apiParametreleri(filtre), [filtre]);
  return { filtre, degistir, temizle, apiParams };
}
